// Thin API client: cookie credentials, CSRF double-submit header, structured errors.
export const API_URL = import.meta.env.VITE_PUBLIC_API_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ErrorEnvelope {
  code: string;
  message: string;
  details?: unknown;
}

/** Reads the API's `{ error: { code, message, details } }` envelope defensively. */
export function parseErrorEnvelope(body: unknown, status: number): ErrorEnvelope {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    const err: unknown = body.error;
    if (typeof err === 'object' && err !== null) {
      const e = err as { code?: unknown; message?: unknown; details?: unknown };
      return {
        code: typeof e.code === 'string' ? e.code : 'HTTP_ERROR',
        message: typeof e.message === 'string' ? e.message : `Request failed (${status})`,
        details: e.details,
      };
    }
  }
  return { code: 'HTTP_ERROR', message: `Request failed (${status})` };
}

let csrfToken: string | null = null;

async function fetchCsrf(): Promise<string> {
  const res = await fetch(`${API_URL}/api/v1/auth/csrf`, { credentials: 'include' });
  const body = (await res.json()) as { token?: unknown };
  if (typeof body.token !== 'string') {
    throw new ApiError(res.status, 'CSRF_UNAVAILABLE', 'Could not start a secure session');
  }
  csrfToken = body.token;
  return body.token;
}

export async function api<T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: unknown } = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') {
    headers['x-csrf-token'] = csrfToken ?? (await fetchCsrf());
    // Only claim JSON when a body is sent: Fastify rejects an empty JSON body with a 400.
    if (init.body !== undefined) headers['content-type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const text = await res.text();
  const json: unknown = text.length > 0 ? JSON.parse(text) : null;
  if (!res.ok) {
    const envelope = parseErrorEnvelope(json, res.status);
    if (envelope.code.startsWith('FST_CSRF')) csrfToken = null;
    throw new ApiError(res.status, envelope.code, envelope.message, envelope.details);
  }
  return json as T;
}

/** Human-readable message for any thrown value. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'RATE_LIMITED') {
      const wait =
        typeof err.details === 'object' &&
        err.details !== null &&
        'retryAfterSeconds' in err.details
          ? (err.details as { retryAfterSeconds?: unknown }).retryAfterSeconds
          : undefined;
      return typeof wait === 'number'
        ? `Too many attempts. Try again in ${String(wait)} s.`
        : 'Too many attempts. Try again in a minute.';
    }
    if (Array.isArray(err.details) && err.details.every((d) => typeof d === 'string')) {
      return `${err.message}: ${err.details.join('; ')}`;
    }
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}
